import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import { ArrowBack, CheckCircle } from '@mui/icons-material';
import { authService } from '../services/firebase';

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [step, setStep] = useState<'form' | 'sent'>('form');

  const handleSendLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.includes('@')) {
      setError('Lütfen geçerli bir e-posta adresi girin.');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // The URL to redirect back to after email verification.
      // This URL must be whitelisted in the Firebase Console.
      const actionCodeSettings = {
        url: `${window.location.origin}/finish-registration`,
        handleCodeInApp: true,
      };

      await authService.sendSignInLinkToEmail(email, actionCodeSettings);
      
      // Save the email in localStorage to retrieve it on the finish-registration page.
      window.localStorage.setItem('emailForSignIn', email);
      
      setStep('sent');
      setSuccess(`Doğrulama linki ${email} adresine gönderildi. Lütfen e-postanızı kontrol edin.`);
    } catch (err: any) {
      console.error('Error sending sign-in link:', err);
      setError(err.message || 'Doğrulama linki gönderilirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Kayıt Ol - HatTravel</title>
        <meta name="description" content="HatTravel'a kaydolmak için e-posta adresinizi doğrulayın." />
      </Helmet>
      <Box sx={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="sm">
          <Box sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
              <Button
                startIcon={<ArrowBack />}
                component={RouterLink}
                to="/login"
                sx={{ mb: 2, color: '#667eea', '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.1)' } }}
              >
                Girişe Dön
              </Button>
            </Box>
            <Card sx={{ background: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)', borderRadius: 4, boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)' }}>
              <CardContent sx={{ p: 5 }}>
                <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 200, textAlign: 'center', background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', mb: 2 }}>
                  {step === 'form' ? 'Kayıt Ol' : 'E-postanı Kontrol Et'}
                </Typography>
                <Typography variant="h6" color="#7f8c8d" sx={{ textAlign: 'center', mb: 4, fontWeight: 300 }}>
                  {step === 'form'
                    ? 'Başlamak için e-posta adresinizi girin.'
                    : 'Kaydı tamamlamak için size gönderdiğimiz linke tıklayın.'}
                </Typography>
                
                {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }}>{success}</Alert>}

                {step === 'form' ? (
                  <form onSubmit={handleSendLink}>
                    <TextField
                      fullWidth
                      label="E-posta Adresiniz"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      type="email"
                      sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 3, background: 'rgba(255, 255, 255, 0.8)' } }}
                    />
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : null}
                      sx={{ borderRadius: 3, py: 1.5, backgroundColor: '#005BBD', '&:hover': { backgroundColor: '#004A9F' } }}
                    >
                      {loading ? 'Gönderiliyor...' : 'Doğrulama Linki Gönder'}
                    </Button>
                  </form>
                ) : (
                  <Box sx={{ textAlign: 'center' }}>
                    <CheckCircle sx={{ fontSize: 80, color: '#4caf50', mb: 2 }} />
                    <Typography variant="h6" sx={{ color: '#4caf50' }}>
                      Link Gönderildi!
                    </Typography>
                  </Box>
                )}

                <Box sx={{ mt: 4, textAlign: 'center' }}>
                  <Typography variant="body1" color="#7f8c8d" sx={{ fontWeight: 300 }}>
                    Zaten hesabınız var mı?{' '}
                    <Link component={RouterLink} to="/login" sx={{ color: '#667eea', textDecoration: 'none', fontWeight: 500 }}>
                      Giriş Yap
                    </Link>
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default RegisterPage;